import { useCallback, useEffect, useState } from 'react'
import { listInvoices, resolveInvoicePdfUrl } from '../services/integrations'
import type { InvoiceStatus } from '../services/integrations'
import { useAuth } from '../modules/auth'

export interface Invoice {
    id: string
    number: string | null
    status: InvoiceStatus
    amountDue: number
    amountPaid: number
    currency: string
    createdAt: string
    periodStart: string | null
    periodEnd: string | null
    description: string | null
    hostedInvoiceUrl: string | null
    pdfUrl: string
}

export interface UseGetInvoicesParams {
    status?: InvoiceStatus
    from?: string
    to?: string
    pageSize?: number
}

const useGetInvoices = (
    params: UseGetInvoicesParams = {}
): {
    invoices: Invoice[]
    loading: boolean
    loadingMore: boolean
    error: string | null
    nextCursor: string | null
    hasMore: boolean
    refresh: () => Promise<void>
    loadMore: () => Promise<void>
} => {
    const { status, from, to, pageSize = 20 } = params
    const { currentUser } = useAuth()
    const { organizationId, hasOrganization } = currentUser

    const [invoices, setInvoices] = useState<Invoice[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [nextCursor, setNextCursor] = useState<string | null>(null)

    const fetchPage = useCallback(
        async (cursor?: string) => {
            const orgId = hasOrganization ? organizationId : undefined
            const res = await listInvoices({
                organizationId: orgId,
                status,
                from,
                to,
                limit: pageSize,
                cursor,
            })
            const data = res?.data?.data
            return {
                items: (data?.items ?? []) as Invoice[],
                nextCursor: (data?.nextCursor ?? null) as string | null,
            }
        },
        [hasOrganization, organizationId, status, from, to, pageSize]
    )

    const refresh = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const page = await fetchPage(undefined)
            setInvoices(page.items)
            setNextCursor(page.nextCursor)
        } catch (err: any) {
            setError(err?.response?.data?.message ?? 'Could not load invoices.')
        } finally {
            setLoading(false)
        }
    }, [fetchPage])

    const loadMore = useCallback(async () => {
        if (!nextCursor) return
        try {
            setLoadingMore(true)
            setError(null)
            const page = await fetchPage(nextCursor)
            setInvoices((prev) => [...prev, ...page.items])
            setNextCursor(page.nextCursor)
        } catch (err: any) {
            setError(err?.response?.data?.message ?? 'Could not load more invoices.')
        } finally {
            setLoadingMore(false)
        }
    }, [fetchPage, nextCursor])

    useEffect(() => {
        refresh()
    }, [refresh])

    return {
        invoices,
        loading,
        loadingMore,
        error,
        nextCursor,
        hasMore: nextCursor !== null,
        refresh,
        loadMore,
    }
}

export const downloadInvoicePdf = async (
    invoiceId: string,
    organizationId?: string
): Promise<void> => {
    const res = await resolveInvoicePdfUrl(invoiceId, organizationId)
    const url: string | undefined = res?.data?.data?.url
    if (!url) throw new Error('PDF URL missing in response')
    window.open(url, '_blank', 'noopener')
}

export default useGetInvoices
